import { useState, useRef, useCallback } from "react";

/**
 * Custom hook for detecting swipe gestures
 * @param {Object} options - Configuration options
 * @param {Function} options.onSwipeLeft - Callback when user swipes left
 * @param {Function} options.onSwipeRight - Callback when user swipes right
 * @param {number} options.threshold - Minimum distance (in px) to trigger swipe (default: 50)
 * @param {number} options.edgeThreshold - Maximum starting X position from edge to enable swipe (default: 50)
 * @param {boolean} options.requireEdgeStart - If true, only allow swipes starting from edges (default: false)
 * @param {string} options.excludeSelector - CSS selector for elements to exclude from swipe detection
 * @returns {Object} - Touch handlers and swipe state
 */
export const useSwipe = ({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  edgeThreshold = 50,
  requireEdgeStart = false,
  excludeSelector = null,
  enabled = true,
} = {}) => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [swipeProgress, setSwipeProgress] = useState(0);
  const startXRef = useRef(null);
  const startYRef = useRef(null);
  const isHorizontalSwipeRef = useRef(null);

  const onTouchStart = useCallback((e) => {
    if (!enabled) return;

    // Check if touch started on an excluded element
    if (excludeSelector) {
      const target = e.target;
      if (target.closest(excludeSelector)) {
        setTouchStart(null);
        return;
      }
    }

    const touch = e.targetTouches[0];
    const screenWidth = window.innerWidth;

    // Only enable swipe from edges if requireEdgeStart is true
    if (requireEdgeStart) {
      const isFromLeftEdge = touch.clientX <= edgeThreshold;
      const isFromRightEdge = touch.clientX >= screenWidth - edgeThreshold;

      if (!isFromLeftEdge && !isFromRightEdge) {
        setTouchStart(null);
        return;
      }
    }

    setTouchStart(touch.clientX);
    setTouchEnd(touch.clientX);
    startXRef.current = touch.clientX;
    startYRef.current = touch.clientY;
    isHorizontalSwipeRef.current = null;
    setIsSwiping(false);
    setSwipeProgress(0);
  }, [enabled, edgeThreshold, requireEdgeStart, excludeSelector]);

  const onTouchMove = useCallback((e) => {
    if (!enabled || touchStart === null) return;

    const touch = e.targetTouches[0];
    const currentX = touch.clientX;
    const currentY = touch.clientY;

    // Determine if this is a horizontal or vertical swipe
    if (isHorizontalSwipeRef.current === null) {
      const deltaX = Math.abs(currentX - startXRef.current);
      const deltaY = Math.abs(currentY - startYRef.current);

      if (deltaX > 10 || deltaY > 10) {
        isHorizontalSwipeRef.current = deltaX > deltaY;
      }
    }

    // Only process horizontal swipes
    if (isHorizontalSwipeRef.current === false) {
      return;
    }

    setTouchEnd(currentX);

    const distance = currentX - touchStart;
    const absDistance = Math.abs(distance);

    if (absDistance > 10) {
      setIsSwiping(true);
      setSwipeDirection(distance > 0 ? "right" : "left");
      // Calculate progress as percentage (0-100)
      const progress = Math.min((absDistance / threshold) * 100, 100);
      setSwipeProgress(progress);
    }
  }, [enabled, touchStart, threshold]);

  const onTouchEnd = useCallback(() => {
    if (!enabled || touchStart === null || touchEnd === null) {
      resetSwipe();
      return;
    }

    const distance = touchEnd - touchStart;
    const absDistance = Math.abs(distance);

    // Only trigger if swipe distance exceeds threshold and was horizontal
    if (absDistance >= threshold && isHorizontalSwipeRef.current) {
      if (distance > 0 && onSwipeRight) {
        onSwipeRight();
      } else if (distance < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
    }

    resetSwipe();
  }, [enabled, touchStart, touchEnd, threshold, onSwipeLeft, onSwipeRight]);

  const resetSwipe = useCallback(() => {
    setTouchStart(null);
    setTouchEnd(null);
    setIsSwiping(false);
    setSwipeDirection(null);
    setSwipeProgress(0);
    startXRef.current = null;
    startYRef.current = null;
    isHorizontalSwipeRef.current = null;
  }, []);

  return {
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    },
    isSwiping,
    swipeDirection,
    swipeProgress,
    resetSwipe,
  };
};

export default useSwipe;
